import { useState, useEffect, useCallback } from 'react';
import { devLog } from '../utils/devLog';

// iOS音頻狀態的持久化鍵值
const IOS_AUDIO_STATE_KEY = 'ascii-drum-editor-ios-audio-state';
const IOS_AUDIO_UNLOCK_ATTEMPTS_KEY = 'ascii-drum-editor-ios-audio-attempts';

interface IOSAudioState {
  isUnlocked: boolean;
  lastUnlockTime: number;
  unlockAttempts: number;
  userHasEverUnlocked: boolean; // 用戶是否曾經成功解鎖過
}

interface UseIOSAudioStateReturn {
  iosAudioUnlocked: boolean;
  userHasEverUnlocked: boolean;
  shouldShowUnlockReminder: boolean;
  setIosAudioUnlocked: (unlocked: boolean) => void;
  recordUnlockAttempt: () => void;
  checkAudioContextState: () => boolean;
  getUnlockReminderMessage: () => string;
}

export function useIOSAudioState(): UseIOSAudioStateReturn {
  // 從localStorage載入狀態
  const loadStoredState = (): IOSAudioState => {
    try {
      const stored = localStorage.getItem(IOS_AUDIO_STATE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      devLog('⚠️ Failed to load iOS audio state from localStorage:', error);
    }
    
    return {
      isUnlocked: false,
      lastUnlockTime: 0,
      unlockAttempts: 0,
      userHasEverUnlocked: false
    };
  };

  const [audioState, setAudioState] = useState<IOSAudioState>(loadStoredState);
  const [currentSessionUnlocked, setCurrentSessionUnlocked] = useState<boolean>(false);

  // 檢查當前音頻上下文狀態
  const checkAudioContextState = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;
    
    try {
      // 檢查Tone.js的狀態
      const Tone = (window as any).Tone;
      if (Tone && Tone.context) {
        const isRunning = Tone.context.state === 'running';
        devLog('🔊 Audio context state check:', { 
          state: Tone.context.state, 
          isRunning,
          currentSessionUnlocked,
          storedUserHasEverUnlocked: audioState.userHasEverUnlocked
        });
        return isRunning;
      }
    } catch (error) {
      devLog('⚠️ Error checking audio context:', error);
    }
    
    return false;
  }, [currentSessionUnlocked, audioState.userHasEverUnlocked]);

  // 儲存狀態到localStorage
  const saveState = useCallback((newState: IOSAudioState) => {
    try {
      localStorage.setItem(IOS_AUDIO_STATE_KEY, JSON.stringify(newState));
      devLog('💾 iOS audio state saved:', newState);
    } catch (error) {
      devLog('⚠️ Failed to save iOS audio state:', error);
    }
  }, []);

  // 設定iOS音頻解鎖狀態
  const setIosAudioUnlocked = useCallback((unlocked: boolean) => {
    devLog('🔊 Setting iOS audio unlocked:', unlocked);
    
    setCurrentSessionUnlocked(unlocked);
    
    if (unlocked) {
      const newState: IOSAudioState = {
        ...audioState,
        isUnlocked: true,
        lastUnlockTime: Date.now(),
        userHasEverUnlocked: true
      };
      setAudioState(newState);
      saveState(newState);
    } else {
      // 當設為false時，只更新當前session狀態，但保留用戶曾經解鎖過的記錄
      const newState: IOSAudioState = {
        ...audioState,
        isUnlocked: false
      };
      setAudioState(newState);
      saveState(newState);
    }
  }, [audioState, saveState]);

  // 記錄解鎖嘗試
  const recordUnlockAttempt = useCallback(() => {
    const newState: IOSAudioState = {
      ...audioState,
      unlockAttempts: audioState.unlockAttempts + 1
    };
    setAudioState(newState);
    saveState(newState);
  }, [audioState, saveState]);

  // 檢查是否應該顯示解鎖提醒
  const shouldShowUnlockReminder = (): boolean => {
    // 非iOS設備不需要提醒
    if (!/iPhone|iPad|iPod/.test(navigator.userAgent)) {
      return false;
    }

    // 當前session已解鎖，不需要提醒
    if (currentSessionUnlocked) {
      return false;
    }

    // 檢查音頻上下文是否真的在運行
    const isAudioContextRunning = checkAudioContextState();
    if (isAudioContextRunning) {
      return false;
    }

    // 如果用戶曾經解鎖過，但當前session未解鎖，則需要提醒
    return audioState.userHasEverUnlocked;
  };

  // 獲取解鎖提醒消息
  const getUnlockReminderMessage = (): string => {
    if (audioState.userHasEverUnlocked) {
      return 'Your iOS audio session has been reset. Please tap the unlock button to restore audio playback.';
    } else {
      return 'iOS requires user interaction to enable audio. Please tap the unlock button to start.';
    }
  };

  // 監聽頁面可見性變化，檢測可能的音頻上下文重置
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && audioState.userHasEverUnlocked) {
        // 頁面重新可見時，檢查音頻上下文狀態
        setTimeout(() => {
          const isStillRunning = checkAudioContextState();
          if (!isStillRunning && currentSessionUnlocked) {
            devLog('⚠️ Audio context lost after visibility change, marking as unlocked=false');
            setCurrentSessionUnlocked(false);
          }
        }, 500); // 給一點時間讓音頻上下文恢復
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [audioState.userHasEverUnlocked, currentSessionUnlocked, checkAudioContextState]);

  // 監聽pagehide事件（iOS Safari特有）
  useEffect(() => {
    const handlePageHide = () => {
      if (currentSessionUnlocked) {
        devLog('📱 Page hiding, iOS audio context may reset');
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [currentSessionUnlocked]);

  return {
    iosAudioUnlocked: currentSessionUnlocked,
    userHasEverUnlocked: audioState.userHasEverUnlocked,
    shouldShowUnlockReminder: shouldShowUnlockReminder(),
    setIosAudioUnlocked,
    recordUnlockAttempt,
    checkAudioContextState,
    getUnlockReminderMessage
  };
} 